import { identity } from "lodash";
import { LocalDateFieldModel, OptionFieldModel, RadioFieldModel } from "../../common/commonForms";
import { makeAutoObservable } from "mobx";
import { toLocalDateISOString } from "../../utils/Dates";
import { TMessage } from "../../i18n/tt";
import { PortfolioCompany } from "../../stores/PortfolioStore";
import { Portfolio } from "../../common/common";
import {
  CompanySelectorContextOption,
  CompanySelectorContextType,
  prepCompanySelectorContextOptions,
} from "./portfolioCompanySelectorDomain";

enum ExportContent {
  ALL_DETAILS = "full",
  ALL_DETAILS_WITH_COSTPRICE = "full_with_costprice",
  PUBLIC = "public",
}

enum ExportLanguage {
  NO = "no",
  EN = "en",
  BOTH = "dual_en_no",
}

export enum ExportFormat {
  EXCEL = "EXCEL",
  PDF = "PDF",
  HTML = "HTML",
}

export const exportFormatLabels: { [key in ExportFormat]: TMessage } = {
  [ExportFormat.EXCEL]: { key: "shareholders.exportFormat.excel" },
  [ExportFormat.PDF]: { key: "shareholders.exportFormat.pdf" },
  [ExportFormat.HTML]: { key: "shareholders.exportFormat.html" },
};

const contentLabels: { [key in ExportContent]: TMessage } = {
  [ExportContent.ALL_DETAILS]: { key: "shareholders.exportContent.all-details" },
  [ExportContent.ALL_DETAILS_WITH_COSTPRICE]: {
    key: "shareholders.exportContent.all-details-with-costprice",
  },
  [ExportContent.PUBLIC]: { key: "shareholders.exportContent.public" },
};

const exportLanguageLabels: { [key in ExportLanguage]: TMessage } = {
  [ExportLanguage.NO]: { key: "shareholders.exportLanguage.no" },
  [ExportLanguage.EN]: { key: "shareholders.exportLanguage.en" },
  [ExportLanguage.BOTH]: { key: "shareholders.exportLanguage.dual_en_no" },
};

export type ExportShareholderData = {
  language: string;
  format: string;
  date: string;
  exportFormat: ExportFormat;
  organizationnumbers?: string[];
};

export class ExportShareholderModel {
  exportStatus: "init" | "loading" | "success" | "error" = "init";
  content: RadioFieldModel<ExportContent>;
  language: RadioFieldModel<ExportLanguage>;
  exportDate: LocalDateFieldModel;
  exportFormat: RadioFieldModel<ExportFormat>;
  onSubmit: (data: ExportShareholderData, onSuccess: () => void, onFailure: () => void) => void;
  onClose: () => void;

  allPortfolios: Portfolio[];
  allAdminCompanies: PortfolioCompany[];
  companySelectorContext: RadioFieldModel<CompanySelectorContextOption | null>;
  companies: OptionFieldModel<PortfolioCompany>;
  isCustomRole: boolean;

  constructor(
    onSubmit: (data: ExportShareholderData, onSuccess: () => void, onFailure: () => void) => void,
    onClose: () => void,
    adminCompanies: PortfolioCompany[],
    portfolios: Portfolio[],
    isCustomRole: boolean,
    selectedContext?: string,
  ) {
    this.content = new RadioFieldModel({
      value: ExportContent.ALL_DETAILS,
      options: Object.values(ExportContent),
      labelFn: (item) => contentLabels[item],
      idFn: identity,
      onChange: (item) => {
        if (item === ExportContent.ALL_DETAILS_WITH_COSTPRICE) {
          this.exportFormat.setValue(ExportFormat.EXCEL);
        }
      },
      disabledFn: () => this.isLoading,
    });
    this.language = new RadioFieldModel({
      value: ExportLanguage.NO,
      options: Object.values(ExportLanguage),
      labelFn: (item) => exportLanguageLabels[item],
      idFn: identity,
      disabledFn: () => this.isLoading,
    });
    this.exportDate = new LocalDateFieldModel({
      value: toLocalDateISOString(Date.now()),
    });
    this.exportFormat = new RadioFieldModel({
      value: ExportFormat.EXCEL,
      options: Object.values(ExportFormat),
      labelFn: (item) => exportFormatLabels[item],
      idFn: identity,
      disabledFn: (item) =>
        this.isLoading ||
        this.content.value === ExportContent.ALL_DETAILS_WITH_COSTPRICE ||
        (item === ExportFormat.HTML && this.hasSelectedMultipleCompanies),
    });

    this.onClose = onClose;
    this.onSubmit = onSubmit;

    this.allPortfolios = portfolios;
    this.allAdminCompanies = adminCompanies;
    this.companySelectorContext = new RadioFieldModel({
      value: this.selectedCompanySelectorContextOption(selectedContext),
      options: [...this.companySelectorContextOptions(selectedContext), null],
      labelFn: (item) =>
        item ? { translated: item.name } : { key: "shareholders.exportContent.show-all" },
      idFn: (item) => item?.id ?? "all",
      iconFn: (item) => item?.icon ?? "globe",
      onChange: () => this.companies.toggleAll(true),
    });
    this.companies = new OptionFieldModel({
      options: () => this.filteredCompanies,
      selectedIds: this.filteredCompanies.map((company) => company.organisationNumber),
      idFn: (item) => item.organisationNumber,
      labelFn: (item) => ({ translated: item.name }),
      onChange: () => this.updateExportFormat(),
    });
    this.isCustomRole = isCustomRole;

    makeAutoObservable(this, {}, { autoBind: true });
  }

  get hasSelectedMultipleCompanies() {
    return this.companies.selected.length > 1;
  }

  selectedCompanySelectorContextOption(selectedContext?: string) {
    return (
      this.companySelectorContextOptions(selectedContext).find((p) => p.id === selectedContext) ??
      null
    );
  }

  companySelectorContextOptions(selectedContext?: string): CompanySelectorContextOption[] {
    const companyContext = this.allAdminCompanies.find(
      (c) => c.organisationNumber === selectedContext,
    );
    return prepCompanySelectorContextOptions(this.portfoliosWithCompanies, companyContext);
  }

  get portfoliosWithCompanies() {
    return this.allPortfolios.filter((portfolio) =>
      this.allAdminCompanies.some((company) => company.portfolioId === portfolio.id),
    );
  }

  updateExportFormat() {
    if (this.hasSelectedMultipleCompanies && this.exportFormat.value === ExportFormat.HTML) {
      this.exportFormat.setValue(ExportFormat.EXCEL);
    }
  }

  get filteredCompanies() {
    const selectedCompanySelectorContext = this.companySelectorContext.value;
    return selectedCompanySelectorContext
      ? this.allAdminCompanies.filter(
          (company) =>
            (selectedCompanySelectorContext.type === CompanySelectorContextType.PORTFOLIO &&
              selectedCompanySelectorContext.id === company.portfolioId) ||
            (selectedCompanySelectorContext.type === CompanySelectorContextType.COMPANY &&
              selectedCompanySelectorContext.id === company.organisationNumber),
        )
      : this.allAdminCompanies;
  }

  get isBatchExport() {
    return this.allAdminCompanies.length > 1 && !this.isCustomRole;
  }

  handleSubmitExport() {
    if (this.exportDate.isValid) {
      this.setExportStatus("loading");
      this.onSubmit(
        this.toData,
        () => {
          this.setExportStatus("success");
          this.onClose();
        },
        () => this.setExportStatus("error"),
      );
    }
  }

  get toData(): ExportShareholderData {
    return {
      language: this.language.value,
      format: this.content.value,
      date: this.exportDate.validatedValue ?? "",
      exportFormat: this.exportFormat.value,
      organizationnumbers: this.isCustomRole
        ? undefined
        : this.companies.selected.map((c) => c.organisationNumber),
    };
  }

  setExportStatus(status: "init" | "loading" | "success" | "error") {
    this.exportStatus = status;
  }

  get isLoading() {
    return this.exportStatus === "loading";
  }

  get isSubmitDisabled() {
    return !this.exportDate.isValid || this.companies.isNoneSelected;
  }
}
